import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import AdminNav from "@/components/AdminNav";
import LingentFloatingIcon from "@/components/LingentFloatingIcon";
import ComposerPanel from "@/components/ComposerPanel";
import VoiceRecorder from "@/components/VoiceRecorder";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Trash2, Wand2, Loader2, Calendar as CalendarIcon } from "lucide-react";
import { format } from "date-fns";

export default function WorkLogPage() {
  const [logs, setLogs] = useState([]);
  const [bulletsText, setBulletsText] = useState("");
  const [tags, setTags] = useState("");
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [saving, setSaving] = useState(false);

  const [recapDays, setRecapDays] = useState(7);
  const [recapLoading, setRecapLoading] = useState(false);
  const [recap, setRecap] = useState(null);
  const [composerOpen, setComposerOpen] = useState(false);
  const [seedArticle, setSeedArticle] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/worklog", { params: { days: 60 } });
      setLogs(data);
    } catch (e) {
      toast.error("Failed to load work log");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    const bullets = bulletsText
      .split("\n")
      .map((s) => s.replace(/^[-*•]\s*/, "").trim())
      .filter(Boolean);
    if (bullets.length === 0) {
      toast.error("Add at least one bullet");
      return;
    }
    setSaving(true);
    try {
      await api.post("/worklog", {
        date,
        bullets,
        tags: tags.split(",").map((t) => t.trim()).filter(Boolean),
      });
      toast.success("Saved");
      setBulletsText("");
      setTags("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this entry?")) return;
    try {
      await api.delete(`/worklog/${id}`);
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const generateRecap = async () => {
    setRecapLoading(true);
    setRecap(null);
    try {
      const { data } = await api.post("/worklog/weekly-post", { days: recapDays });
      setRecap(data);
      toast.success(`Drafted from ${data.source_logs} entries`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Recap failed");
    } finally {
      setRecapLoading(false);
    }
  };

  const openInComposer = () => {
    if (!recap) return;
    setSeedArticle({
      topic: `Weekly recap (${recapDays}d)`,
      notes: "",
      category: "General AI",
      title: recap.title,
      body: recap.body,
      hashtags: recap.hashtags,
      cover_image: null,
      status: "draft",
      id: null,
    });
    setComposerOpen(true);
  };

  return (
    <div className="min-h-screen bg-ink text-bone">
      <AdminNav />
      <main className="max-w-[1500px] mx-auto px-6 lg:px-10 py-10">
        <div className="label-mono text-safety">// work log</div>
        <h1 className="font-display text-4xl md:text-5xl font-black tracking-tightest mt-2">
          What did you ship today?
        </h1>
        <p className="mt-3 text-bone/60 max-w-2xl">
          1–3 bullets per day. End of week, Lingent turns them into a sellable LinkedIn post.
        </p>

        <div className="mt-10 grid lg:grid-cols-12 gap-8">
          {/* Left: add entry */}
          <div className="lg:col-span-5 space-y-4 border border-hairline bg-surface p-6">
            <div>
              <Label className="label-mono text-bone/70">Date</Label>
              <Input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="mt-2 rounded-none bg-ink border-hairline focus-visible:ring-1 focus-visible:ring-klein h-11"
                data-testid="worklog-date-input"
              />
            </div>
            <div>
              <Label className="label-mono text-bone/70">Bullets (one per line)</Label>
              <Textarea
                rows={6}
                value={bulletsText}
                onChange={(e) => setBulletsText(e.target.value)}
                placeholder={"- Shipped YOLOv9 pilot for bottle defect detection\n- Customer call with cement plant CTO\n- Refactored audio pipeline for noisy floor"}
                className="mt-2 rounded-none bg-ink border-hairline focus-visible:ring-1 focus-visible:ring-klein font-mono text-sm"
                data-testid="worklog-bullets-input"
              />
            </div>
            <div>
              <Label className="label-mono text-bone/70">Tags (comma separated, optional)</Label>
              <Input
                value={tags}
                onChange={(e) => setTags(e.target.value)}
                placeholder="computer-vision, customer-call"
                className="mt-2 rounded-none bg-ink border-hairline focus-visible:ring-1 focus-visible:ring-klein h-11"
                data-testid="worklog-tags-input"
              />
            </div>
            <Button
              onClick={save}
              disabled={saving}
              data-testid="worklog-save-btn"
              className="w-full rounded-none bg-klein hover:bg-klein-hover text-white label-mono h-11"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Add entry
            </Button>

            {/* Voice memo */}
            <div className="pt-2">
              <VoiceRecorder
                onTranscribed={(d) => {
                  load();
                  if (d.draft) {
                    setSeedArticle({
                      topic: "From a voice memo",
                      notes: "",
                      category: "General AI",
                      title: d.draft.title,
                      body: d.draft.body,
                      hashtags: d.draft.hashtags,
                      cover_image: null,
                      status: "draft",
                      id: null,
                    });
                    setComposerOpen(true);
                  }
                }}
              />
            </div>
          </div>

          {/* Right: timeline */}
          <div className="lg:col-span-7">
            <div className="flex items-center justify-between border-b border-hairline pb-3 mb-4">
              <div className="label-mono text-bone/60">Recent entries · {logs.length}</div>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min={1}
                  max={30}
                  value={recapDays}
                  onChange={(e) => setRecapDays(Number(e.target.value) || 7)}
                  className="w-20 rounded-none bg-surface border-hairline h-9"
                  data-testid="recap-days-input"
                />
                <Button
                  onClick={generateRecap}
                  disabled={recapLoading}
                  data-testid="recap-generate-btn"
                  className="rounded-none bg-safety hover:bg-safety/90 text-white label-mono h-9"
                >
                  {recapLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wand2 className="w-4 h-4 mr-2" />}
                  Make sellable post
                </Button>
              </div>
            </div>

            {recap && (
              <div className="border border-safety bg-surface p-5 mb-6" data-testid="recap-output">
                <div className="label-mono text-safety mb-2">// LINGENT DRAFT</div>
                <h3 className="font-display text-2xl font-bold tracking-tightest" data-testid="recap-title">
                  {recap.title}
                </h3>
                <p className="mt-3 whitespace-pre-wrap text-bone/85 text-sm leading-relaxed">
                  {recap.body}
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {recap.hashtags?.map((h, i) => (
                    <Badge key={i} className="rounded-none bg-klein text-white label-mono">{h}</Badge>
                  ))}
                </div>
                <Button
                  onClick={openInComposer}
                  data-testid="recap-open-composer-btn"
                  className="mt-4 rounded-none bg-klein hover:bg-klein-hover text-white label-mono"
                >
                  Open in composer
                </Button>
              </div>
            )}

            <div className="space-y-3" data-testid="worklog-list">
              {logs.length === 0 && (
                <div className="border border-hairline p-10 text-center text-bone/50">
                  No entries yet. Add today's bullets on the left.
                </div>
              )}
              {logs.map((l) => (
                <div key={l.id} className="border border-hairline bg-surface p-4 flex items-start gap-4" data-testid={`worklog-entry-${l.id}`}>
                  <div className="label-mono text-safety w-24 shrink-0 flex items-center gap-1">
                    <CalendarIcon className="w-3 h-3" />
                    {format(new Date(l.date), "MMM dd")}
                  </div>
                  <div className="flex-1">
                    <ul className="list-disc list-outside ml-4 text-bone/85 text-sm space-y-1">
                      {l.bullets.map((b, i) => (<li key={i}>{b}</li>))}
                    </ul>
                    {l.tags?.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {l.tags.map((t, i) => (
                          <span key={i} className="label-mono text-[10px] text-bone/50">#{t}</span>
                        ))}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => del(l.id)}
                    className="text-bone/40 hover:text-red-400"
                    data-testid={`worklog-delete-${l.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>

      <LingentFloatingIcon onOpen={() => { setSeedArticle(null); setComposerOpen(true); }} />
      <ComposerPanel
        open={composerOpen}
        onClose={() => { setComposerOpen(false); setSeedArticle(null); }}
        onSaved={() => { setComposerOpen(false); setSeedArticle(null); toast.success("Saved to articles"); }}
        article={seedArticle}
      />
    </div>
  );
}
